import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type NowOrderStatus = 'confirmed' | 'pharmacist-review' | 'preparing' | 'packed' | 'out-for-delivery' | 'delivered'
export type SubstitutionPreference = 'allow' | 'contact' | 'cancel'
export type TemperatureTelemetryStatus = 'verified' | 'pending' | 'unavailable' | 'exception'

interface CartItem {
  id: number
  name: string
  price: number
  quantity: number
  image: string
  brand?: string
  requiresPrescription?: boolean
  prescriptionStatus?: 'uploaded' | 'reviewing'
  coldChain?: boolean
  nowEligible?: boolean
  availabilityStatus?: 'available' | 'unavailable'
  temperatureRange?: string
}

interface Product {
  id: number
  name: string
  price: number
  image: string
  brand?: string
  requiresPrescription?: boolean
  prescriptionStatus?: 'uploaded' | 'reviewing'
  coldChain?: boolean
  nowEligible?: boolean
  availabilityStatus?: 'available' | 'unavailable'
  temperatureRange?: string
}

interface ActiveNowOrder {
  id: string
  status: NowOrderStatus
  estimatedDelivery: string
  createdAt: string
  deliveryInstructions: string
  cancelled: boolean
  receiptConfirmed: boolean
  telemetryStatus: TemperatureTelemetryStatus
  temperature?: number
  temperatureRange?: string
  telemetryUpdatedAt?: string
  vehicleLabel: string
  isDelayed?: boolean
  revisedEstimatedDelivery?: string
}

interface StoreState {
  cart: CartItem[]
  wishlist: Product[]
  language: 'en' | 'ar'
  isAuthenticated: boolean
  activeNowOrder: ActiveNowOrder | null
  substitutionPreference: SubstitutionPreference
  setLanguage: (lang: 'en' | 'ar') => void
  setAuthenticated: (isAuthenticated: boolean) => void
  addToCart: (product: Product) => void
  removeFromCart: (productId: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  toggleWishlist: (product: Product) => void
  isInWishlist: (productId: number) => boolean
  clearCart: () => void
  cartTotal: () => number
  cartCount: () => number
  searchQuery: string
  setSearchQuery: (query: string) => void
  recentSearches: string[]
  addRecentSearch: (query: string) => void
  clearRecentSearches: () => void
  setSubstitutionPreference: (preference: SubstitutionPreference) => void
  createNowOrder: (orderId?: string) => void
  updateNowOrderStatus: (status: NowOrderStatus) => void
  flagNowOrderDelay: (revisedEstimatedDelivery: string) => void
  setNowDeliveryInstructions: (instructions: string) => void
  cancelNowOrder: () => void
  confirmNowReceipt: () => void
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      cart: [],
      wishlist: [],
      language: 'en',
      isAuthenticated: false,
      searchQuery: '',
      recentSearches: [],
      activeNowOrder: null,
      substitutionPreference: 'contact',

      setLanguage: (lang) => set({ language: lang }),
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setSearchQuery: (query) => set({ searchQuery: query }),

      addRecentSearch: (query) => {
        const normalizedQuery = query.trim()
        if (!normalizedQuery) return
        const filtered = get().recentSearches.filter((search) => search.toLowerCase() !== normalizedQuery.toLowerCase())
        set({ recentSearches: [normalizedQuery, ...filtered].slice(0, 5) })
      },

      clearRecentSearches: () => set({ recentSearches: [] }),

      addToCart: (product) => {
        const cart = get().cart
        const existingItem = cart.find((item) => item.id === product.id)
        if (existingItem) {
          set({ cart: cart.map((item) => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item) })
          return
        }
        set({ cart: [...cart, { ...product, quantity: 1 }] })
      },

      removeFromCart: (productId) => set({ cart: get().cart.filter((item) => item.id !== productId) }),

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId)
          return
        }
        set({ cart: get().cart.map((item) => item.id === productId ? { ...item, quantity } : item) })
      },

      toggleWishlist: (product) => {
        const wishlist = get().wishlist
        const exists = wishlist.some((item) => item.id === product.id)
        set({ wishlist: exists ? wishlist.filter((item) => item.id !== product.id) : [...wishlist, product] })
      },

      isInWishlist: (productId) => get().wishlist.some((item) => item.id === productId),
      clearCart: () => set({ cart: [] }),
      cartTotal: () => get().cart.reduce((total, item) => total + item.price * item.quantity, 0),
      cartCount: () => get().cart.reduce((total, item) => total + item.quantity, 0),
      setSubstitutionPreference: (preference) => set({ substitutionPreference: preference }),
      createNowOrder: (orderId) => set({
        activeNowOrder: {
          id: orderId ?? `CHILL-${Math.floor(100000 + Math.random() * 899999)}`,
          status: 'confirmed',
          estimatedDelivery: 'Within 120 minutes',
          createdAt: new Date().toISOString(),
          deliveryInstructions: 'Leave at the door if unavailable',
          cancelled: false,
          receiptConfirmed: false,
          telemetryStatus: 'pending',
          temperatureRange: '2–8°C',
          telemetryUpdatedAt: 'Awaiting box seal',
          vehicleLabel: 'Temperature-controlled vehicle · 4821',
          isDelayed: true,
          revisedEstimatedDelivery: 'Arriving in 35 min',
        },
      }),
      updateNowOrderStatus: (status) => set((state) => ({ activeNowOrder: state.activeNowOrder ? {
        ...state.activeNowOrder,
        status,
        telemetryStatus: status === 'out-for-delivery' || status === 'delivered' ? 'verified' : 'pending',
        temperature: status === 'out-for-delivery' || status === 'delivered' ? 5.1 : undefined,
        telemetryUpdatedAt: status === 'out-for-delivery' || status === 'delivered' ? 'Just now' : 'Awaiting box seal',
      } : null })),
      flagNowOrderDelay: (revisedEstimatedDelivery) => set((state) => ({ activeNowOrder: state.activeNowOrder ? { ...state.activeNowOrder, isDelayed: true, revisedEstimatedDelivery } : null })),
      setNowDeliveryInstructions: (deliveryInstructions) => set((state) => ({ activeNowOrder: state.activeNowOrder ? { ...state.activeNowOrder, deliveryInstructions } : null })),
      cancelNowOrder: () => set((state) => ({ activeNowOrder: state.activeNowOrder ? { ...state.activeNowOrder, cancelled: true } : null })),
      confirmNowReceipt: () => set((state) => ({ activeNowOrder: state.activeNowOrder ? { ...state.activeNowOrder, receiptConfirmed: true } : null })),
    }),
    { name: 'dawaak-pharmacy-storage' },
  ),
)
